/**
 * @typedef {import('socket.io').Socket} WebSocketIO
 * @typedef {import('./ArchitectureElement')} ArchitectureElement
 * @typedef {import('./AdAccount')} AdAccount
 * @typedef {import('./AdrielCampaign')} AdrielCampaign
 * @typedef {import('./Campaign')} Campaign
 * @typedef {import('./AdSet')} AdSet
 * @typedef {import('./Ad')} Ad
 * @typedef {import('./Report')} Report
 * @typedef {import('./Project')} Project
 */

const R = require('ramda');
const moment = require('moment');
const { uniq, getIntersection } = require('../utils');
const {
  AdAccount,
  Campaign,
  AdSet,
  Ad,
  Dimension,
  DimensionElement,
  Report,
  Project,
  // AdrielCampaign,
} = require('.');
const AdrielCampaign = require('./AdrielCampaign');
const metricUtils = require('../utils/metricUtils');

const { TRACKER_LIST } = require('../constants');
const {
  mergeActions,
  transformGAMetric,
  getAdrielStatus,
  getMetricFromList,
  sortByStartDate,
  getListMinTime,
  getListMaxTime,
  sumObject,
  mergeConversionsByTracker,
  mergeSubChannelData,
} = require('../utils/utils');
const archUtil = require('../utils/utils');
const ArchitectureElement = require('./ArchitectureElement');
const { formatCampaign } = require('../dashboardHelpers');

const sumMetric = (acc, { metric }) => sumObject(acc, metric);

function capitalStr(phrase) {
  return phrase.replace(/^\w/, function (c) {
    return c.toUpperCase();
  });
}

const CLASS_LEVEL_MAPPING = {
  adAccount: require('./AdAccount'),
  adrielCampaign: require('./AdrielCampaign'),
  campaign: require('./Campaign'),
  adSet: require('./AdSet'),
  ad: require('./Ad'),
  keyword: require('./Keyword'),
};

class ArchitectureFilter {
  /** @type {'active'|'deactivated'|null} The status of the campaigns to be filtered*/
  status = null;
  /** @type {['facebook'|'google'|'naver'|'kakao']} */
  channels = null;
  /** @type {[string]} Strings that must be inside the campaign name. */
  titles = null;
  /** @type {[string]} The list of campaign ids that we should report about. If empty, we report about all campaigns. */
  campaignIds = null;
}

class DateFilter {
  /** @type {string} The start date of the report with format 'YYYY-MM-DD'. */
  from = null;
  /** @type {string} The end date of the report with format 'YYYY-MM-DD'. */
  to = null;
}

class AdAccountArchitecture {
  /**
   *
   * @param {object} obj
   * @param {WebSocketIO} webSocket
   */
  constructor(obj, webSocket = null) {
    // super({architecture: null, webSocket})
    this.webSocket = webSocket;
    this.registerWebSocket({ webSocket, doListenToMessages: true });
    this.init();
    // FE only and debug purpose
    if (typeof window !== 'undefined') {
      // @ts-ignore
      window.architecture = this;
    }
  }

  init() {
    /** @type {Object.<string, Object.<string, Object>>} */
    this.KPIByIdByLevel = {
      project: {},
      channel: {},
      campaign: {},
      dimensionElement: {},
      customDimension: {},
      adSet: {},
      ad: {},
      adrielCampaign: {},
    };
    this.dimensionById = {};
    this.dimensionElementById = {};

    this.dimensionElementIdByDimensionIdByCampaignId = {};
    this.dimensionIdByDimensionElementId = {};

    this.clear();

    // initiate static dimensions
    this.registerDimension({ id: 'channel', name: 'Channel' }, false);
    this.registerDimension({ id: 'objective', name: 'Objective' }, false);
  }

  // setFilter(filter) {
  //     this.filter = filter || {};
  //     this.filterData();
  // }

  clear() {
    // /** @type {Object.<string, AdrielCampaign>} */
    // this.adrielCampaignsById = {};
    const elementsByIdByProject = (this.elementsByIdByLevel && this.elementsByIdByLevel['project']) || {};

    /** @type {Object.<string, Object.<string, ArchitectureElement>>} */
    this.elementsByIdByLevel = {
      project: elementsByIdByProject,
      adAccount: {},
      adrielCampaign: {},
      campaign: {},
      adSet: {},
      ad: {},
      keyword: {},
    };

    this.adrielCampaignConnectIdByChannelCampaignId = {};

    /** @type {Object.<string, Object.<string, string>>} */
    this.parentIdByLevelByChildId = {};
    /** @type {Object.<string, Object.<string, string[]>>} */
    this.childrenIdsByLevelByParentId = {};
    /** @type {Object.<string, string[]>} */
    this.channelCampaignIdsByConnectId = {};

    /** @type {Object.<string, string[]>} */
    this.pixelEventsByAdAccountId = {};

    /** @type {Date} */
    this.updatedAt = null;

    /** @type {ArchitectureFilter} campaignFilter */
    this.campaignFilter = { status: null, channels: null, campaignIds: null, titles: null };

    /** @type {DateFilter} dateFilter */
    this.dateFilter = { from: null, to: null };

    /** @type {'day'|'week'|'month'} */
    this.dateGroup;

    /** @type {Array<string>} */
    this.actionAttributionWindows = [];

    /** @type {Boolean} */
    this.isArchitectureLoaded = false;

    this.clearReports();
  }

  clearReports({ doClearGraphReports = true } = {}) {
    /** @type {Boolean} */
    this.isReportsLoaded = false;
    this.isDetailsReportsLoaded = false;

    if (doClearGraphReports) {
      /** @type {Boolean} */
      this.isGraphLoaded = false;
      this.isSubChannelGraphLoaded = false;
    }

    /** @type {Object.<string, Lead[]>} */
    this.leadsById = {};

    /** @type {Object.<string, Report[]>} */
    this.GAReportsById = {};

    /** @type {Object.<string, Report[]>} */
    this.reportsById = {};

    /** @type {Object.<string, Object.<string, Report[]>>} */
    this.reportsByIdByDetail = {
      subChannel: {},
      newAndReturning: {},
      age: {},
      gender: {},
      location: {},
      searchKeywords: {},
      deviceTarget: {},
    };

    /** @type {Report[]} */ // obsolete
    this.reports = [];

    /** @type {Object.<string, Object.<string, object>>} */
    this.formattedByIdByLevel = {
      adrielCampaign: {},
      campaign: {},
      adSet: {},
    };

    this.availableMetrics = [];
    this.availableConversionNames = [];
    if (doClearGraphReports) this.clearGraphReports();
  }
  clearGraphReports() {
    this.isGraphLoaded = false;
    this.isSubChannelGraphLoaded = false;
    /** @type {Object.<string, Object.<string, Object.<string, Report[]>>>} */
    this.GAReportsByIdByDateGroup = { day: {}, week: {}, month: {} };
    /** @type {Object.<string, Object.<string, Report[]>>} */
    this.reportsByIdByDateGroup = { day: {}, week: {}, month: {} };
    /** @type {Object.<string, Object.<string, Object.<string, object>>>} */
    this.formattedByIdByDateGroupByLevel = {
      campaign: { day: {}, week: {}, month: {} },
    };
    /** @type {Object.<string, Object.<string, Object.<string, Report[]>>>} */
    this.reportsByIdByDetailByDateGroup = { day: {}, week: {}, month: {} };
  }

  getSchedule() {
    const schedules = this.campaigns.map(campaign => campaign.getSchedule()).filter(Boolean);
    if (schedules.length === 0) return null;
    return archUtil.mergeSchedules(schedules);
  }

  getCreationDate() {
    const dates = this.campaigns.map(R.prop('createdAt')).filter(Boolean);
    if (dates.length === 0) return moment().add(-30, 'days').format('YYYY-MM-DD');
    return dates.reduce((dateA, dateB) => (new Date(dateA) < new Date(dateB) ? dateA : dateB));
  }

  getDateFilter() {
    const creationDate = this.getCreationDate();
    let { from, to } = this.filter;
    if (!from) from = creationDate || moment().diff(30, 'days').format('YYYY-MM-DD');
    if (!to) to = new Date();
    return { from, to };
  }

  /**
   * @return {number}
   */
  getFilterDateRangeInDays() {
    const { from, to } = this.getDateFilter();
    return moment(to).diff(moment(from), 'days');
  }

  /**
   * @return {string[]}
   */
  getAvailableDateGroups() {
    return ['day', 'week', 'month'];
  }

  /**
   * @return {Object.<string, AdAccount>}
   */
  get adAccountsById() {
    // @ts-ignore
    return this.elementsByIdByLevel['adAccount'];
  }

  /**
   * @return {Object.<string, AdrielCampaign>}
   */
  get adrielCampaignsById() {
    // @ts-ignore
    return this.elementsByIdByLevel['adrielCampaign'] || [];
  }

  /**
   * @return {Object.<string, Project>}
   */
  get projectsById() {
    // @ts-ignore
    return this.elementsByIdByLevel['project'] || [];
  }

  /**
   * @return {Object.<string, Campaign>}
   */
  get campaignsById() {
    // @ts-ignore
    return this.elementsByIdByLevel['campaign'];
  }

  /**
   * @return {Object.<string, AdSet>}
   */
  get adSetsById() {
    // @ts-ignore
    return this.elementsByIdByLevel['adSet'];
  }

  /**
   * @return {Object.<string, Ad>}
   */
  get adsById() {
    // @ts-ignore
    return this.elementsByIdByLevel['ad'];
  }

  get from() {
    return R.prop('from')(this.dateFilter);
  }

  get to() {
    return R.prop('to')(this.dateFilter);
  }

  // code for retro compatibility, directly accessing campaigns, adSets, etc.. is deprecated because it is slow
  get adAccounts() {
    return Object.values(this.adAccountsById);
  }

  get dimensions() {
    return Object.values(this.dimensionById);
  }

  get adrielCampaigns() {
    return Object.values(this.adrielCampaignsById);
  }

  get projects() {
    return Object.values(this.projectsById);
  }

  get campaigns() {
    return Object.values(this.campaignsById);
  }

  get adSets() {
    return Object.values(this.adSetsById);
  }

  get ads() {
    return Object.values(this.adsById);
  }

  getNbOfCampaigns() {
    return this.adrielCampaigns.length + this.campaigns.filter(campaign => !campaign.connectId).length;
  }

  /**
   * Get adriel campaign ids and channel campaign ids
   * @return {Array<string>} campaignIds
   */
  getCampaignIds() {
    return this.adrielCampaigns
      .map(adrielCampaign => adrielCampaign.id)
      .concat(this.customCampaigns.map(campaign => campaign.id));
  }

  getLastRefresh() {
    return new Date().toISOString();
  }

  get customCampaigns() {
    return this.campaigns.filter(campaign => campaign.isCustom);
  }

  getConversionsByTracker() {
    return mergeConversionsByTracker(this.campaigns.map(campaign => campaign.getConversionsByTracker()));
  }

  getKPI({ id, level, channel }) {
    const info = archUtil.getInfoFromId(id);
    if (!level) level = info.level;
    if (!channel) channel = info.channel;
    const KPIById = this.KPIByIdByLevel[level];
    if (!KPIById) return null;
    if (info) {
      if (level === 'campaign') {
        id = `${channel}:${info.channelId}`;
      } else if (level === 'channel' && info.channel) {
        id = `${info.channel}:${info.channelId}`;
      }
    }
    return KPIById[id];
  }

  getSubChannelList({ includeFacebookSubChannels = true } = {}) {
    return Array.from(
      new Set(
        this.campaigns.flatMap(campaign =>
          !includeFacebookSubChannels && campaign.isFacebook ? 'facebook' : campaign.subChannels,
        ),
      ),
    );
  }

  getDataBySubChannel({ dateGroup, cellFormat = true } = {}) {
    const conversionAndMetricList = this.getCampaignsAndAdAccountWithReports('subChannel', dateGroup)
      .flatMap(campaign =>
        campaign.getConversionAndMetric({
          dateGroup,
          detail: 'subChannel',
        }),
      )
      .filter(Boolean);
    // group the date per date and per subChannel ('text' prop)
    const conversionAndMetricPerDateAndSubchannel = conversionAndMetricList.reduce((acc, conversionAndMetric) => {
      const { from, text: subChannel } = conversionAndMetric;
      const key = dateGroup ? `${from}:${subChannel}` : subChannel;
      if (!acc[key]) acc[key] = [];
      acc[key].push(conversionAndMetric);
      return acc;
    }, {});
    // merge date with same from date and subChannel
    const conversionsAndMetricPerDate = Object.values(conversionAndMetricPerDateAndSubchannel).map(
      conversionAndMetricList => metricUtils.mergeMetricAndConversion(conversionAndMetricList),
    );
    const dataBySubChannel = R.groupBy(R.prop('text'), conversionsAndMetricPerDate);
    if (!dateGroup) {
      if (cellFormat) {
        return conversionsAndMetricPerDate.map(el => ({
          cell: metricUtils.getComputedMetric({
            metric: el.metric,
            metricList: this.availableMetrics,
            conversionsByTracker: el.conversionsByTracker,
          }),
          subChannel: el.text,
        }));
      } else {
        return conversionsAndMetricPerDate.map(el => ({
          metric: el.metric,
          conversionsByTracker: el.conversionsByTracker,
          subChannel: el.text,
        }));
      }
    } else
      return Object.entries(dataBySubChannel).map(([subChannel, data]) => ({
        subChannel,
        conversionsAndMetricPerDate: data,
      }));
  }

  /** @type {Set<string>} All the messages from eventIds in this set will be ignored.*/
  eventIdsToIgnore = new Set();

  latestEventIdByMessageName = {};

  eventIdCount = 0;

  webSocketCallbacks = {};

  /**
   * Emits a message to the websocket which contain the given message name and data.
   * Adds to data a unique event id that contains the message name nad the optional function name.
   * @param {object} p
   * @param {string} p.messageName A message name for the websocket (Similar to a route).
   * @param {object} p.data The data to be sent.
   * @param {string} [p.functionName] (optional) A name to be included in the event id. (useful for debugging).
   * @param {WebSocketIO} [p.webSocket] (optional) The websocket to use. Otherwise, it uses the websocket registered using this.registerWebSocket.
   * @param {function} [p.callback] (optional) This callback will be called for each replies (identified by the event id) with the arguments: (messageName, messageData).
   */
  emitToWebSocket({ messageName, data, functionName = undefined, webSocket = this.webSocket, callback = undefined }) {
    if (!webSocket) return;
    if (this.latestEventIdByMessageName[messageName]) {
      this.eventIdsToIgnore.add(this.latestEventIdByMessageName[messageName]);
    }
    let eventId = this.getNewEventId([functionName, messageName].join('-'));
    this.latestEventIdByMessageName[messageName] = eventId;
    webSocket.emit(messageName, { ...data, eventId });
    // Save the callback by event id in order to call it later.
    if (callback) {
      this.webSocketCallbacks[eventId] = callback;
    }
  }

  /**
   * Get a new unique event id to be sent to the websocket server.
   * All replies should contain this unique event id.
   * @param {string} name (optional) A string that should be included in the event id.
   * @returns {string}
   */
  getNewEventId(name = undefined) {
    this.eventIdCount++;
    if (name) return name + '-' + this.eventIdCount;
    return '' + this.eventIdCount;
  }

  clearForUpdateCampaignFilter({ getArchitectureEventId, getReportsEventId }) {
    if (this.latestGetArchitectureEventId) this.eventIdsToIgnore.add(this.latestGetArchitectureEventId);
    if (this.latestGetReportsEventId) this.eventIdsToIgnore.add(this.latestGetReportsEventId);
    this.latestGetArchitectureEventId = getArchitectureEventId;
    this.latestGetReportsEventId = getReportsEventId;
    this.clear();
  }

  updateCampaignFilterToWebSocket({
    filter,
    dateFilter = this.dateFilter,
    dateGroup = this.dateGroup,
    webSocket = this.webSocket,
    actionAttributionWindows = [],
  }) {
    this.clear();
    this.actionAttributionWindows = actionAttributionWindows;
    this.campaignFilter = filter;
    this.dateFilter = dateFilter;
    this.dateGroup = dateGroup;
    this.emitToWebSocket({
      webSocket,
      messageName: 'getArchitecture',
      functionName: 'updateCampaignFilter',
      data: {
        filter: this.campaignFilter,
        dateFilter: this.dateFilter,
      },
    });
    // this.emitToWebSocket({
    //     webSocket,
    //     messageName: "getReports",
    //     functionName: "updateCampaignFilter",
    //     data: {
    //         filter: this.campaignFilter,
    //         dateFilter: this.dateFilter,
    //         actionAttributionWindows: this.actionAttributionWindows,
    //     }
    // })
    // this.emitToWebSocket({
    //     webSocket,
    //     messageName: "getAds",
    //     functionName: "updateCampaignFilter",
    //     data: {
    //         filter: this.campaignFilter,
    //         dateFilter: this.dateFilter,
    //     }
    // })
    // if (dateGroup)
    //     this.emitToWebSocket({
    //         webSocket,
    //         messageName: "getGraphs",
    //         functionName: "updateCampaignFilter",
    //         data: {
    //             filter: this.campaignFilter,
    //             dateFilter: this.dateFilter,
    //             dateGroup: this.dateGroup,
    //             actionAttributionWindows: this.actionAttributionWindows,
    //         }
    //     })
  }

  updateDateFilterToWebSocket({
    dateFilter,
    filter = this.campaignFilter,
    dateGroup = this.dateGroup,
    webSocket = this.webSocket,
    actionAttributionWindows = this.actionAttributionWindows,
  }) {
    this.clearReports({ doClearGraphReports: Boolean(dateGroup) });
    this.campaignFilter = filter;
    this.dateFilter = dateFilter;
    this.dateGroup = dateGroup;
    this.actionAttributionWindows = actionAttributionWindows;
    this.emitToWebSocket({
      webSocket,
      messageName: 'getArchitecture',
      functionName: 'updateCampaignFilter',
      data: {
        filter: this.campaignFilter,
        dateFilter: this.dateFilter,
      },
    });
    // this.emitToWebSocket({
    //     webSocket,
    //     messageName: "getReports",
    //     functionName: "updateDateFilter",
    //     data: {
    //         filter: this.campaignFilter,
    //         dateFilter: this.dateFilter,
    //         actionAttributionWindows: this.actionAttributionWindows,
    //     }
    // })
    // this.emitToWebSocket({
    //     webSocket,
    //     messageName: "getAds",
    //     functionName: "updateDateFilter",
    //     data: {
    //         filter: this.campaignFilter,
    //         dateFilter: this.dateFilter,
    //     }
    // })
    // if (dateGroup)
    //     this.emitToWebSocket({
    //         webSocket,
    //         messageName: "getGraphs",
    //         functionName: "updateDateFilter",
    //         data: {
    //             filter: this.campaignFilter,
    //             dateFilter: this.dateFilter,
    //             dateGroup: this.dateGroup,
    //             actionAttributionWindows: this.actionAttributionWindows,
    //         }
    //     })
  }

  updateDateGroupToWebSocket({
    dateGroup,
    dateFilter = this.dateFilter,
    filter = this.campaignFilter,
    webSocket = this.webSocket,
    actionAttributionWindows = this.actionAttributionWindows,
  }) {
    this.clearGraphReports();
    this.campaignFilter = filter;
    this.dateFilter = dateFilter;
    this.dateGroup = dateGroup;
    this.actionAttributionWindows = actionAttributionWindows;
    // if (dateGroup)
    //     this.emitToWebSocket({
    //         webSocket,
    //         messageName: "getGraphs",
    //         functionName: "updateDateGroup",
    //         data: {
    //             filter: this.campaignFilter,
    //             dateFilter: this.dateFilter,
    //             dateGroup: this.dateGroup,
    //             actionAttributionWindows: this.actionAttributionWindows,
    //         }
    //     })
  }

  /**
   *
   * @param {ArchitectureElement} element
   * @returns {ConnectArchitecture}
   */
  getElementPartialArchitecture(element, useUpdateElementFormat = true) {
    let partialArchi = {
      adAccount: {
        channelId: element.adAccount.channelId,
        channel: element.adAccount.channel,
        currency: element.adAccount.currency,
        assetId: element.adAccount.assetId,
      },
    };
    if (element.channelCampaignId)
      if (useUpdateElementFormat)
        // no 's', no array
        partialArchi.campaign = { channelId: element.channelCampaignId };
      else partialArchi.campaigns = [{ channelId: element.channelCampaignId }];
    if (element.channelAdSetId) partialArchi.adSets = [{ channelId: element.channelAdSetId }];
    if (element.channelAdId) partialArchi.ads = [{ channelId: element.channelAdId }];
    if (element.level != 'campaign') partialArchi[element.level + 's'] = [{ channelId: element.channelId }];
    return partialArchi;
  }

  /**
   * Edit an element. (editable dashboard.)
   * For example, turn on or off an ad.
   * @param {object} p
   * @param {string} p.elementId If of the element that should be edited. (ex: 'facebook:adSet:123123')
   * @param {string} [p.level] (optional)
   * @param {'running'} p.type Field that should be edited.
   * @param {any} p.value New value of the edited field.
   * @param {function} p.callback This function will be called when the update is done. callback({isSuccess, data, error})
   * @param {WebSocketIO} [p.webSocket] (optional) Web socket to send the data through. Will use pre-registered web socket by default.
   * @example
   *      architecture.updateElementToWebSocket({
   *          elementId: "facebook:campaign:123123",
   *          type: "running", // on-off button
   *          value: false, // turning off
   *          callBack({isSuccess, data, error}){
   *              if(isSuccess)
   *                  triggerSuccess()
   *              else
   *                  triggerFailure(error)
   *          }
   *      })
   */
  updateElementToWebSocket({ elementId, level, type, value, webSocket = this.webSocket, callback = undefined }) {
    let element = this.getElement({ level, elementId });
    let action = {
      channelId: element.channelId,
      level: level || element.level,
      type,
      value,
    };
    if (level == 'adrielCampaign')
      action = {
        ...action,
        campaignId: elementId,
      };
    else {
      action = {
        ...action,
        channel: element.adAccount.channel,
        adAccountId: element.adAccount.channelId,
        partialArchitecture: this.getElementPartialArchitecture(element),
      };
    }
    this.emitToWebSocket({
      webSocket,
      messageName: 'updateElement',
      data: {
        actions: [action],
      },
      callback(messageName, messageData) {
        if (messageName == 'updateElement.data') {
          let actionData = messageData.actions[0];
          let isSuccess = actionData.status == 'COMPLETED';
          callback({ isSuccess, data: actionData, error: actionData.error });
        }
      },
    });
  }

  getChildrenLevel(level) {
    if (level == 'adrielCampaign') return 'campaign';
    else if (level == 'campaign') return 'adSet';
    else if (level == 'adSet') return 'ad';
    else console.warn(`Unknown level '${level}'`);
  }

  getElementChildrenFromWebSocket({ elementId, dateFilter = this.dateFilter, webSocket = this.webSocket }) {
    let element = this.getElement({ elementId });
    let adAccount = {
      channel: element.channel,
      channelId: element.adAccount.channelId,
      currency: element.adAccount.currency,
    };

    let architectureFilter = {
      adAccount,
      [element.level + 's']: [
        {
          channelId: element.channelId,
        },
      ],
    };

    let level = this.getChildrenLevel(element.level);
    this.emitToWebSocket({
      messageName: 'getArchitectureChildren',
      data: {
        architectureFilter,
        level,
        dateFilter,
        actionAttributionWindows: this.actionAttributionWindows,
      },
      webSocket,
    });
  }

  /**
   * register children architecture elements
   * @param {Object} opts
   * @param {string} opts.parentId
   * @param {string} opts.childId
   * @param {string} opts.parentLevel
   * @param {string} opts.childLevel
   */
  registerChildren({ parentId, childId, parentLevel, childLevel }) {
    if (parentLevel) {
      if (!this.parentIdByLevelByChildId[childId]) {
        this.parentIdByLevelByChildId[childId] = {};
      }
      this.parentIdByLevelByChildId[childId][parentLevel] = parentId;
    }
    if (!this.childrenIdsByLevelByParentId[parentId])
      this.childrenIdsByLevelByParentId[parentId] = {
        project: [],
        campaign: [],
        adSet: [],
        ad: [],
        keyword: [],
      };
    let childrenIds = this.childrenIdsByLevelByParentId[parentId][childLevel];
    // if (!childrenIds.includes(childId)) {
    childrenIds.push(childId);
    // }
  }

  getElement({ channel = undefined, level = undefined, channelId = undefined, elementId = undefined }) {
    if (elementId) {
      if (!level) level = elementId.toString().split(':')[1];
    } else {
      elementId = archUtil.generateId({
        channel,
        level,
        channelId,
      });
    }
    return this.elementsByIdByLevel[level][elementId];
  }

  registerDimension(element, isCustom = true) {
    const { id, name, dimensionElements } = element;
    const dimension = new Dimension({ id, name, architecture: this, isCustom });
    if (dimensionElements) {
      dimensionElements.forEach(dimensionElement => {
        this.registerDimensionElement({
          element: dimensionElement,
          dimension,
        });
      });
    }
    this.dimensionById[id] = dimension;
    return dimension;
  }

  registerDimensionElement({ element, dimension, dimensionElement }) {
    const campaignIds = element?.campaignIds || [];
    if (!dimensionElement) {
      const { id, name } = element;
      dimensionElement = new DimensionElement({
        id,
        name,
        architecture: this,
      });
    }
    // link dimension to dimension element
    this.dimensionIdByDimensionElementId[dimensionElement.id] = dimension.id;
    dimension.addDimensionElement(dimensionElement);
    this.dimensionElementById[dimensionElement.id] = dimensionElement;
    if (campaignIds) dimensionElement.addCampaignIds(campaignIds);
    return dimensionElement;
  }

  registerAdrielCampaign({ element }) {
    // create adriel campaign dimension
    let dimension = this.dimensionById['adrielCampaign'];
    if (!dimension) {
      dimension = new Dimension({
        id: 'adrielCampaign',
        name: 'adrielCampaign',
        architecture: this,
      });
      this.dimensionById[dimension.id] = dimension;
    }
    const elementsById = this.elementsByIdByLevel['adrielCampaign'];
    const adrielCampaign = new AdrielCampaign({
      ...element,
      architecture: this,
    });
    elementsById[element.id] = adrielCampaign;
    this.registerDimensionElement({
      dimension,
      dimensionElement: adrielCampaign,
    });
    if (element.campaignIds) {
      adrielCampaign.updateCampaignIds(element.campaignIds);
    }
  }

  registerKPI(kpi) {
    const { kpiValue, metric, level, elementId } = kpi;
    const KPIById = this.KPIByIdByLevel[level];
    if (!KPIById[elementId]) KPIById[elementId] = {};
    const elementKPI = KPIById[elementId];
    elementKPI[metric] = kpiValue;
  }

  deleteKPI(kpi) {
    const { metric, level, elementId } = kpi;
    const KPIById = this.KPIByIdByLevel[level];
    if (!KPIById[elementId]) KPIById[elementId] = {};
    const elementKPI = KPIById[elementId];
    delete elementKPI[metric];
  }

  registerElement({ element, level, channel, adAccountId }) {
    if (!element) {
      console.warn('registerElement null', {
        element,
        level,
        channel,
        adAccountId,
      });
      return;
    }
    const classType = CLASS_LEVEL_MAPPING[level];
    // Generate a unique id for this element
    const elementId = archUtil.generateId({
      channel,
      level,
      channelId: element.channelId,
    });
    // Check if this element is already here
    let elementInstance = this.elementsByIdByLevel[level][elementId];
    if (elementInstance) {
      // If it already exists, just update data.
      element.id = elementId;
      elementInstance.updateData(element);
    } else {
      // If it does not exist, create this element
      element.id = elementId;
      // Create a new instance.
      elementInstance = new classType(element);
      // Attach the architecture to this instance.
      elementInstance.architecture = this;
      // Add the instance in our store.
      this.elementsByIdByLevel[level][elementId] = elementInstance;
      // Register the parent-children relationship of this element.
      const levelAndParentIds = archUtil.getLevelAndParentIds({
        element,
        level,
        channel,
        adAccountId,
      });
      levelAndParentIds.forEach(({ level: parentLevel, parentId }) =>
        this.registerChildren({
          parentId,
          childId: elementId,
          parentLevel,
          childLevel: level,
        }),
      );

      // for campaign we need to create some dimension elements for the static dimensions (channel, objective)
      if (level === 'campaign') {
        // register the dimension elements of the channel dimension
        const channelDimension = this.dimensionById['channel'];
        // choose either subChannel or channel
        const channel =
          !elementInstance.isFacebook && elementInstance.subChannels?.length === 1
            ? elementInstance.subChannels[0]
            : elementInstance.channel;

        if (!this.dimensionElementById[channel]) {
          this.registerDimensionElement({
            element: { id: channel, name: channel },
            dimension: channelDimension,
          });
        }

        const channelDimensionElement = this.dimensionElementById[channel];
        channelDimensionElement.addCampaignId(element.id);

        // register the dimension elements of the objective dimension
        const objectiveDimension = this.dimensionById['objective'];
        if (objectiveDimension) {
          const objective = elementInstance.objective;
          if (!this.dimensionElementById[objective]) {
            this.registerDimensionElement({
              element: { id: objective, name: objective },
              dimension: objectiveDimension,
            });
          }
          const objectiveDimensionElement = this.dimensionElementById[objective];
          objectiveDimensionElement.addCampaignId(element.id);
        }

        // link the campaign to the adrielCampaign
        if (elementInstance.connectId) {
          const connectId = elementInstance.connectId;
          if (!this.channelCampaignIdsByConnectId[connectId]) this.channelCampaignIdsByConnectId[connectId] = [];
          this.channelCampaignIdsByConnectId[connectId].push(elementId);
          this.adrielCampaignConnectIdByChannelCampaignId[element.id] = connectId;
        }
      }
    }
  }

  registerReports({ adAccountId, reports, level, channel, detail, dateGroup, isLastChunk }) {
    // if (reports.length === 0) return;
    let reportsById;
    if (detail) {
      let reportsByIdByDetail = this.reportsByIdByDetail;
      if (dateGroup) {
        reportsByIdByDetail = this.reportsByIdByDetailByDateGroup[dateGroup];
      }
      if (!reportsByIdByDetail[detail]) reportsByIdByDetail[detail] = {};
      reportsById = reportsByIdByDetail[detail];
    } else {
      if (dateGroup) {
        reportsById = this.reportsByIdByDateGroup[dateGroup];
      } else reportsById = this.reportsById;
    }
    const elementIds = [];
    // TODO actions with 0 conversion
    reports.forEach(report => {
      if (report.actions) {
        if (!this.pixelEventsByAdAccountId[adAccountId]) this.pixelEventsByAdAccountId[adAccountId] = [];
        const pixelEvents = this.pixelEventsByAdAccountId[adAccountId];
        report.actions.forEach(action => {
          action.conversions = action.value;
          action.spendOnActions = report.metric.spend;
          action.clicksOnActions = report.metric.clicks;
          action.spend = report.metric.spend;
          action.clicks = report.metric.clicks;
        });
        const reportPixelEvents = report.actions.filter(action => action.type === 'pixel_event');
        reportPixelEvents.forEach(event => {
          if (!pixelEvents.includes(event.name)) pixelEvents.push(event.name);
        });
      }
      const elementChannelId = archUtil.getLevelChannelId(report, level);
      const elementId = archUtil.generateId({
        channel,
        level,
        channelId: elementChannelId,
      });
      // reset the reports
      if (!elementIds.includes(elementId) || !reportsById[elementId]) reportsById[elementId] = [];
      reportsById[elementId].push(report);
      elementIds.push(elementId);
    });
    if (['adAccount', 'campaign'].includes(level) && !detail) {
      this.updateAvailableMetricsAndConversions();
      if (level === 'campaign' && isLastChunk) {
        const adAccount = this.adAccountsById[`${channel}:adAccount:${adAccountId}`];
        if (adAccount) {
          if (dateGroup) {
            adAccount.isGraphLoaded = true;
          } else {
            adAccount.isReportsLoaded = true;
          }
        } else {
          console.warn(`architecture element ${channel}:adAccount:${adAccountId} is missing`);
        }
      }
    }
  }

  registerGAData({ sources, dateGroup }) {
    this.googleAnalyticsDataAttached = true;
    if (!sources || sources.length === 0) return;
    sources = sources.filter(
      source =>
        source.connectAdId ||
        source.channelCampaignId ||
        source.adrielCampaignId ||
        source.channelAdSetId ||
        source.channelAdId,
    );
    const eventNames = uniq(
      sources
        .map(source => source.events)
        .filter(Boolean)
        .flat()
        .map(event => event.event),
    );
    sources.forEach(source => {
      const { from, to } = source;
      let report = { subChannels: source.subChannels, from, to };
      const gaMetric = transformGAMetric(source.metric);
      report.metric = gaMetric; //archUtil.sumObject(gaMetric, report.metric);
      const events = source.events;
      if (events) {
        const existingEventNames = events.map(event => event.event);
        eventNames.forEach(eventName => {
          if (!existingEventNames.includes(eventName)) {
            events.push({
              event: eventName,
              users: 0,
            });
          }
        });
        // if (!report.actions) report.actions = [];

        const formatEvent = event => ({
          key: null,
          type: 'ga_event',
          name: event.event,
          value: parseInt(event.users),
          conversions: parseInt(event.users),
        });
        report.actions = events.map(formatEvent);
      } else report.actions = [];
      for (let level of ['ad', 'adSet', 'campaign']) {
        let channelIdName = `channel${capitalStr(level)}Id`;
        let channelId = source[channelIdName];
        if (!channelId) continue;
        let id = archUtil.generateId({
          channel: source.channel,
          level,
          channelId: channelId,
        });
        if (dateGroup) {
          if (!this.GAReportsByIdByDateGroup[dateGroup][id]) this.GAReportsByIdByDateGroup[dateGroup][id] = [];
          // @ts-ignore
          this.GAReportsByIdByDateGroup[dateGroup][id].push(report);
        } else {
          if (!this.GAReportsById[id]) this.GAReportsById[id] = [];
          // @ts-ignore
          this.GAReportsById[id].push(report);
        }
      }
    });
    this.updateAvailableMetricsAndConversions();
  }

  /**
   * Register a websocket connected to the backend.
   * If a websocket is registered, this instance can send and receive messages automatically.
   * @param {object} p
   * @param {WebSocketIO} p.webSocket A websocket connected to app.adriel.com/ws/architecture
   * @param {boolean} p.doListenToMessages If true, this instance will listen to all messages from the socket and register new elements and reports send by the backend.
   */
  registerWebSocket({ webSocket, doListenToMessages = true }) {
    this.webSocket = webSocket;
    if (doListenToMessages && this.webSocket) {
      // Trick to allow wild card listeners. https://stackoverflow.com/questions/10405070/socket-io-client-respond-to-all-events-with-one-handler
      const onevent = webSocket.onevent;
      webSocket.onevent = function (packet) {
        const args = packet.data || [];
        onevent.call(this, packet); // original call
        packet.data = ['*'].concat(args);
        if (packet.data.length == 2) packet.data.push({});
        onevent.call(this, packet); // additional call to catch-all
      };
      // Listen to all messages and call 'updateFromWebsocket'
      this.webSocket.on('*', (messageName, messageData, rest) => {
        // fix for strange bug
        if (messageName == '*' && rest) {
          messageName = messageData;
          messageData = rest;
        }
        // Update the architecture instance
        this.updateFromWebSocket(messageName, messageData);
        if (messageData.eventId && this.webSocketCallbacks[messageData.eventId])
          this.webSocketCallbacks[messageData.eventId](messageName, messageData);
      });
    }
  }

  registerElementsFromArchitecture({ partialArchi, levels = null, level = null, adAccountId = null, channel = null }) {
    if (!adAccountId) adAccountId = partialArchi?.adAccount?.channelId;
    if (!channel) channel = partialArchi?.adAccount?.channel;
    if (!level && !levels) levels = ['campaign', 'adSet', 'ad'];

    if (levels && levels.length > 0) {
      for (const level of levels) {
        this.registerElementsFromArchitecture({ partialArchi, level, levels: null, adAccountId, channel });
      }
    } else {
      if (partialArchi.adAccount) {
        // Ad accounts are handled separately because messages have only one ad account but the other elements of the architecture have several ones.
        const adAccount = partialArchi.adAccount;
        if (partialArchi.campaigns)
          adAccount.campaignIds = partialArchi.campaigns.map(campaign => `${adAccount.channel}:${campaign.channelId}`);
        this.registerElement({
          element: partialArchi.adAccount,
          level: 'adAccount',
          channel,
          adAccountId,
        });
      }
      // Find the right field for this architecture level.
      const levelToField = {
        ad: 'ads',
        adSet: 'adSets',
        campaign: 'campaigns',
        adAccount: 'adAccount',
        adrielCampaign: 'adrielCampaigns',
        keyword: 'keywords',
      };
      const elementName = levelToField[level];
      let elements = partialArchi[elementName];
      if (!elements) return;
      if (elementName === 'adrielCampaigns') {
        // Adriel campaigns do not belong to any ad account and are handled separately.
        elements.map(element =>
          this.registerAdrielCampaign({
            element,
          }),
        );
      } else {
        // Register each element one by one.
        elements.map(element =>
          this.registerElement({
            element,
            level,
            channel,
            adAccountId,
          }),
        );
      }
    }
  }

  updateFromWebSocket(messageName, messageData) {
    // Ignore error messages.
    if (messageName.endsWith('.error')) return;
    const isCompletedMessage = messageName.endsWith('.completed');
    messageName = messageName.replace(/\.(data|completed)$/, '');
    // Parse JSON messages
    if (typeof messageData == 'string')
      try {
        messageData = JSON.parse(messageData);
      } catch (error) {
        console.error(error);
      }
    // Ignore old messages.
    if (typeof messageData == 'object' && messageData.eventId) {
      if (this.eventIdsToIgnore.has(messageData.eventId)) {
        console.log(`${messageName} ignored`, messageData.eventId);
        return;
      }
    }
    if (isCompletedMessage) {
      console.log('completed', messageName);
      if (messageName == 'architecture') {
        this.isArchitectureLoaded = true;
      } else if (messageName == 'reports') {
        this.isReportsLoaded = true;
      } else if (messageName == 'reportsPerDate') {
        this.isGraphLoaded = true;
      } else if (messageName === 'subChannelReportsPerDate') {
        this.isSubChannelGraphLoaded = true;
      } else if (messageName === 'detailsReports') {
        this.isDetailsReportsLoaded = true;
      }
      return;
    }
    if (messageName == 'campaignsInfo') {
      // Campaigns info are just for the front end and will be removed after the filter bar is deployed.
    } else if (messageName == 'architecture') {
      const { partialArchi, level, levels, adAccountId, channel } = messageData;
      this.registerElementsFromArchitecture({ partialArchi, level, levels, adAccountId, channel });
    } else if (
      ['reports', 'detailsReports', 'subChannelReports', 'reportsPerDate', 'subChannelReportsPerDate'].includes(
        messageName,
      )
    ) {
      const { adAccountId, reports, channel, level, dateGroup, detail, isLastChunk } = messageData;
      this.registerReports({
        adAccountId,
        reports,
        channel,
        level,
        dateGroup,
        detail,
        isLastChunk,
      });
    } else if (['analyticsReports', 'analyticsReportsPerDate'].includes(messageName)) {
      const { dateGroup, sources } = messageData;
      this.registerGAData({ dateGroup, sources });
    } else if (messageName === 'dimensions') {
      const { dimensions } = messageData;
      dimensions.forEach(dimension => this.registerDimension(dimension));
    } else if (messageName === 'KPI') {
      const { KPIList } = messageData;
      KPIList.forEach(kpi => this.registerKPI(kpi));
    } else {
      console.warn(`Unknown websocket message with name:"${messageName}"`);
    }
  }

  /**
   * Make payload to send to the websocket when the user unfold a campaign or adSet
   * @param {object} p
   * @param {string} p.id id of the campaign or adSet that the user wants to unfold
   * @param {string} p.targetLevel Level that will be shown after the unfolding.
   */
  getArchitectureFilter({ id, targetLevel }) {
    const element = this.getElement({ elementId: id });
    if (!element) throw new Error(`Cannot find element with id '${id}'`);
    const architectureFilter = {
      adAccount: R.pick(['channel', 'channelId', 'currency'], element.adAccount),
    };
    // include campaigns
    if (['ad', 'adSet'].includes(targetLevel)) {
      architectureFilter.campaigns = [{ channelId: element.campaign.channelId }];
    }
    if (targetLevel === 'ad') {
      architectureFilter.adSets = [
        { channelId: element.adSet.channelId, channelCampaignId: element.campaign.channelId },
      ];
    }
    return architectureFilter;
  }

  /**
   * return a list of adAccounts with adAccount-level reports
   * and campaigns that are not within those accounts and have their own reports
   */
  getCampaignsAndAdAccountWithReports(detail = null, dateGroup = null) {
    let adAccountsWithReports = [];
    let hasReportsOnAdAccountLevel = {};
    // if (!dateGroup) {
    //     for (let adAccount of this.adAccounts) {
    //         if (!adAccount.isFacebook && detail === 'subChannel') continue;
    //         let reports = adAccount.getReports({ detail, dateGroup: null });
    //         if (reports && reports.length > 0 && !R.isEmpty(reports[0].metric)) {
    //             hasReportsOnAdAccountLevel[adAccount.channelId] = true
    //             adAccountsWithReports.push(adAccount)
    //         }
    //     }
    // }
    let campaignsWithoutAdAccountLevelReports = this.campaigns.filter(
      campaign => !hasReportsOnAdAccountLevel[campaign.adAccount.channelId],
    );
    return [...adAccountsWithReports, ...campaignsWithoutAdAccountLevelReports];
  }

  get metric() {
    return getMetricFromList(this.getCampaignsAndAdAccountWithReports());
  }

  updateAvailableMetricsAndConversions() {
    this.updateAvailableMetrics();
    this.updateAvailableConversionNames();
  }

  updateAvailableMetrics() {
    // remove wrong conversions data that we get from google
    const metricNames = Object.entries(this.metric)
      .filter(([name, value]) => name !== 'conversions' && value != null)
      .map(([name]) => name);
    if (!metricNames.includes('dailyBudget')) metricNames.push('dailyBudget');
    if (
      metricNames.includes('totalGAConversions') ||
      metricNames.includes('totalPixelConversions') ||
      metricNames.includes('totalOtherConversions')
    ) {
      // metricNames.push('conversions');
    }
    metricNames.push('cpc');
    metricNames.push('ctr');
    metricNames.push('cpm');
    metricNames.push('results', 'cpr', 'cvr');

    // if (metricNames.includes('conversions')) {
    //     metricNames.push('cpa');
    //     metricNames.push('conversionRate');
    // }
    if (metricNames.includes('reach')) {
      metricNames.push('frequency');
    }
    if (metricNames.includes('bouncedVisits')) {
      metricNames.push('bounceRate');
    }
    if (metricNames.includes('gaRevenue')) {
      metricNames.push('gaRoas');
    }
    if (metricNames.includes('pixelRevenue')) {
      metricNames.push('pixelRoas');
    }
    if (metricNames.includes('totalVisitDuration')) {
      metricNames.push('visitDuration');
    }
    if (metricNames.includes('totalPageViews')) {
      metricNames.push('pageViews');
    }
    if (metricNames.includes('videoViews')) {
      metricNames.push('cpv');
      metricNames.push('videoViewsRate');
    }

    if (this.hasLifeTimeBudgetCampaign) {
      metricNames.push('amountSpentPercentage');
    }
    // exclude metrics that we don't want to show
    const availableMetrics = R.reject(name => ['spendOnActions', 'clicksOnActions'].includes(name), metricNames);
    this.availableMetrics = availableMetrics;
  }

  updateAvailableConversionNames() {
    const conversionByTracker = this.getConversionsByTracker();
    const conversionNames = [];
    if (conversionByTracker) {
      TRACKER_LIST.forEach(tracker => {
        if (conversionByTracker[tracker]) {
          conversionByTracker[tracker].forEach(event => conversionNames.push(`${tracker}:${event.name}`));
        }
      });
    }
    this.availableConversionNames = conversionNames;
  }

  get hasLifeTimeBudgetCampaign() {
    return this.adrielCampaigns.some(adrielCampaign => adrielCampaign.isLifeTimeBudget); //|| this.campaigns.some((campaign) => campaign.isLifeTimeBudget)
  }

  get conversions() {
    const mergedConversions = R.compose(
      mergeActions,
      R.filter(Boolean),
      R.flatten,
      R.map(R.prop('conversions')),
    )(this.campaigns);
    return mergedConversions;
  }

  get startDate() {
    return R.compose(
      getListMinTime,
      R.filter(Boolean),
      R.map(campaign => campaign.startDate || campaign.createdAt),
    )(this.campaigns);
  }

  get isContinuously() {
    return this.campaigns.some(R.prop('isContinuously'));
  }

  get endDate() {
    if (this.isContinuously) return null;
    return R.compose(getListMaxTime, R.filter(Boolean), R.map(R.prop('endDate')))(this.campaigns);
  }

  getDetailValues(detailName) {
    const detailValues = [];
    // if (this.adSets.some(adSet => adSet[detailName])) {
    this.getCampaignsAndAdAccountWithReports(detailName).forEach(adAccountOrCampaign => {
      const adAccountOrCampaignDetailData = adAccountOrCampaign.getConversionAndMetric({
        detail: detailName,
        dateGroup: null,
      });
      if (adAccountOrCampaignDetailData && adAccountOrCampaignDetailData.length > 0)
        detailValues.push(
          ...adAccountOrCampaignDetailData.map(el => ({
            // text: el.text,
            ...(detailName === 'searchKeywords' ? { keyword: el.text } : { text: el.text }),
            cell: {
              ...metricUtils.getComputedMetric({
                metric: el.metric,
                metricList: this.availableMetrics,
                conversionsByTracker: el.conversionsByTracker,
              }),
              campaignName: adAccountOrCampaign.level === 'campaign' ? adAccountOrCampaign.name : null,
              adAccountName: adAccountOrCampaign.adAccount.name,
              subChannels: adAccountOrCampaign.subChannels || [adAccountOrCampaign.channel],
            },
          })),
        );
    });
    return detailValues;
  }

  getDetails(detailName) {
    const details = R.flatten(this.campaigns.map(campaign => campaign.getDetails(detailName)).filter(Boolean));
    const metricByDetail = R.reduceBy(sumMetric, {}, R.prop('text'), details);
    const detailsWithMetric = Object.entries(metricByDetail).map(([text, metric]) => ({
      text,
      metric,
    }));
    return detailsWithMetric;
  }

  getAds() {
    const ads = R.flatten(
      this.campaigns.map(campaign => {
        const { adAccount, channelId, name, isCustom, adrielCampaign } = campaign;
        const campaignData = {
          id: channelId,
          title: name,
          isCustom,
          objectives: [campaign.objective],
          adAccount: R.pick(['name'], adAccount),
        };
        if (!isCustom && adrielCampaign) {
          campaignData.id = adrielCampaign.id;
          campaignData.title = adrielCampaign.title;
          campaignData.image = adrielCampaign.image;
          campaignData.objectives = adrielCampaign.objectives;
        }
        return campaign.ads.map(ad => {
          return {
            ...ad.format(),
            campaign: campaignData,
          };
          return {
            campaign: campaignData,
            adAccountId: adAccount.channelId,
            adSetId: ad.adSet && ad.adSet.channelId,
            campaignId: campaign.channelId,
            adId: ad.channelId,
            subChannels: ad.subChannels,
            ...ad,
            adSet: ad.adSet,
            conversionsByTracker: ad.getConversionsByTracker(),
            ...R.pick(['metric', 'status', 'bidCap', 'creative', 'channel'], ad),
          };
        });
      }),
    );
    return ads;
  }

  getConversionGoalMap() {
    const keyNameSet = this.campaigns.map(campaign => campaign.getConversionGoalMap());
    return archUtil.unionNameToKeyMap(keyNameSet);
  }

  getConversionAndMetric({ dateGroup, detail } = {}) {
    const conversionAndMetricPerCampaign = this.campaigns
      .flatMap(campaign => campaign.getConversionAndMetric({ dateGroup, detail }))
      .filter(Boolean);
    if (dateGroup) {
      // group conversion and metric by the from date
      const conversionAndMetricByDate = conversionAndMetricPerCampaign.reduce((acc, conversionAndMetric) => {
        if (!acc[conversionAndMetric.from]) acc[conversionAndMetric.from] = [];
        acc[conversionAndMetric.from].push(conversionAndMetric);
        return acc;
      }, {});
      // for each from date merge the conversion and the metric
      return Object.values(conversionAndMetricByDate).map(conversionAndMetricList => {
        return metricUtils.mergeMetricAndConversion(conversionAndMetricList);
      });
    } else {
      return metricUtils.mergeMetricAndConversion(conversionAndMetricPerCampaign);
    }
  }

  /**
   * @return {Array<{id: string, name: string }} dimensionList
   */
  getDimensionList() {
    return Object.values(this.dimensionById);
  }

  // TODO : REMOVE WHEN THE REST API IS READY
  // /**
  //  * @param {object} params
  //  * @param {import('socket.io').Socket} params.webSocket a websocket connected to app.adriel.com/ws/architecture
  //  * @param {string} params.name dimension name
  //  * @return {{id: string, name: string}} dimension
  //  */
  // async getAndRefreshCampaignPartialArchi({ webSocket = this.webSocket, campaignId }) {
  //     return new Promise((resolve, reject) => {
  //         webSocket.emit('getAndRefreshCampaignPartialArchi', { campaignId }, (error, campaignPartialArchi) => {
  //             if (error) return reject(error);

  //             resolve();
  //         });
  //     });
  // }

  /**
   * Create dimension
   * @param {object} p
   * @param {import('socket.io').Socket} p.webSocket a websocket connected to app.adriel.com/ws/architecture
   * @param {string} p.name dimension name
   * @return {{id: string, name: string}} dimension
   */
  async createDimension({ webSocket = this.webSocket, name }) {
    return new Promise((resolve, reject) => {
      webSocket.emit('createDimension', { name }, (error, element) => {
        if (error) return reject(error);
        const dimension = this.registerDimension(element);
        resolve(dimension.format());
      });
    });
  }

  /**
   * Delete dimension
   * @param {object} p
   * @param {import('socket.io').Socket} p.webSocket a websocket connected to app.adriel.com/ws/architecture
   * @param {string} p.id dimension id
   */
  async deleteDimension({ webSocket = this.webSocket, id }) {
    return new Promise((resolve, reject) => {
      webSocket.emit('deleteDimension', { id }, error => {
        if (error) return reject(error);
        delete this.dimensionById[id];
        resolve();
      });
    });
  }

  /**
   * Update dimension
   * @param {object} p
   * @param {import('socket.io').Socket} p.webSocket a websocket connected to app.adriel.com/ws/architecture
   * @param {Object} p.dimension dimension
   */
  async updateDimension({ webSocket = this.webSocket, dimension }) {
    return new Promise((resolve, reject) => {
      webSocket.emit('updateDimension', dimension, (error, element) => {
        if (error) return reject(error);
        const dimension = this.registerDimension(element);
        resolve(dimension.format());
      });
    });
  }

  /**
   * Create dimension element
   * @param {object} p
   * @param {import('socket.io').Socket} p.webSocket a websocket connected to app.adriel.com/ws/architecture
   * @param {string} p.dimensionId dimensionId
   * @param {string} p.dimensionElementName dimension element name
   */
  async createDimensionElement({ webSocket = this.webSocket, dimensionId, dimensionElementName }) {
    const dimension = this.dimensionById[dimensionId];
    return new Promise((resolve, reject) => {
      webSocket.emit('createDimensionElement', { dimensionId, dimensionElementName }, (error, { id, name }) => {
        if (error) return reject(error);
        const dimensionElement = this.registerDimensionElement({
          element: { id, name },
          dimension,
        });
        resolve(dimensionElement);
      });
    });
  }

  /**
   * Delete dimension
   * @param {object} p
   * @param {import('socket.io').Socket} p.webSocket a websocket connected to app.adriel.com/ws/architecture
   * @param {string} p.id dimensionElement id
   * @param {string} p.dimensionId dimensionId
   */
  async deleteDimensionElement({ webSocket = this.webSocket, dimensionId, id }) {
    return new Promise((resolve, reject) => {
      webSocket.emit('deleteDimensionElement', { id }, error => {
        if (error) return reject(error);
        const dimension = this.dimensionById[dimensionId];
        dimension.deleteDimensionElement(id);
        delete this.dimensionElementById[id];
        resolve();
      });
    });
  }

  /**
   * Update dimension element
   * @param {object} p
   * @param {import('socket.io').Socket} p.webSocket a websocket connected to app.adriel.com/ws/architecture
   * @param {string} p.id dimension element id
   * @param {string} p.name dimension element name
   * @param {Array<string>} p.campaignIds campaign ids for this dimension element
   */
  async updateDimensionElement({ webSocket = this.webSocket, id, name, campaignIds }) {
    const dimensionElement = this.dimensionElementById[id];

    return new Promise((resolve, reject) => {
      webSocket.emit('updateDimensionElement', { id, name, campaignIds }, (error, el) => {
        if (error) return reject(error);
        dimensionElement.name = el.name;
        if (campaignIds) {
          dimensionElement.updateCampaignIds(campaignIds);
        }
        resolve(el);
      });
    });
  }

  /**
   * Get the campaigns grouped by dimensions
   * @param {Array<string>} dimensionIds
   * @return {Object} dimensionTree
   */
  getCampaignDimensionTree(dimensionIds) {
    const rootTree = { campaignDimension: { campaignIds: [] } };
    for (const campaign of this.campaigns) {
      let currentTree = rootTree;
      const dimensionElements = campaign.getDimensionElements(dimensionIds);
      const nbOfElements = dimensionElements.length;
      // the campaign doesn't have any dimension from the list add it in the campaignIds and go to the next campaign
      if (nbOfElements === 0) {
        currentTree.campaignDimension.campaignIds.push(campaign.id);
        continue;
      }

      dimensionElements.forEach((dimensionElement, index) => {
        const isLastDimension = index === nbOfElements - 1;
        const dimensionId = dimensionElement.dimension.id;
        if (!currentTree[dimensionId]) {
          currentTree[dimensionId] = {};
        }
        const currentDimensionTree = currentTree[dimensionId];
        if (!currentDimensionTree[dimensionElement.id]) {
          currentDimensionTree[dimensionElement.id] = {
            campaignIds: [],
            dimensionNode: {
              campaignDimension: { campaignIds: [] },
            },
          };
        }
        currentDimensionTree[dimensionElement.id].campaignIds.push(campaign.id);
        currentTree = currentDimensionTree[dimensionElement.id].dimensionNode;
        if (isLastDimension) {
          currentTree.campaignDimension.campaignIds.push(campaign.id);
        }
      });
    }
    return rootTree;
  }

  getCampaignsPerDimensions({ dimensionIds = [], includeChildren = true, dateGroup = null }) {
    // we get the campaignIds grouped by dimensionIds and the dimensionElementIds
    const dimensionTree = this.getCampaignDimensionTree(dimensionIds);
    const campaignsPerDimensionList = [];
    // we format the elements and we create empty dimension element for the campaigns with no dimension element (label)
    const fillList = ({ tree, list, dimensionIds = [], parentElementTableId = null }) => {
      if (dimensionIds.length === 0) {
        const campaignIds = tree['campaignDimension']?.campaignIds;
        if (!campaignIds) return;
        const formattedCampaigns = campaignIds.map(campaignId => {
          const campaign = this.campaignsById[campaignId];
          const formattedCampaign = campaign.format({
            includeChildren,
            dateGroup,
            parentElementTableId,
          });
          return formattedCampaign;
        });
        // return formattedCampaigns;
        list.push(...formattedCampaigns);
        return;
      }
      const [currentDimensionId, ...restDimensionIds] = dimensionIds;
      const currentDimension = this.dimensionById[currentDimensionId];
      // if there is no dimension elements for the current dimension we skip the current dimension
      if (!tree[currentDimensionId]) {
        return fillList({ tree, list, dimensionIds: restDimensionIds, parentElementTableId });
      } else {
        const emptyDimensionNode = {};
        const emptyDimensionElementCampaignIds = [];
        Object.entries(tree).forEach(([dimensionId, dimensionElementObj]) => {
          if (dimensionId == currentDimensionId) {
            Object.entries(dimensionElementObj).forEach(([dimensionElementId, { campaignIds, dimensionNode }]) => {
              const dimensionElement = this.dimensionElementById[dimensionElementId];

              const formattedDimensionElement = dimensionElement.format({
                dateGroup,
                campaignIds,
                parentElementTableId,
              });
              if (includeChildren) {
                const childrenList = [];
                list.push({
                  ...formattedDimensionElement,
                  children: childrenList,
                });
                fillList({
                  tree: dimensionNode,
                  list: childrenList,
                  dimensionIds: restDimensionIds,
                  parentElementTableId: formattedDimensionElement.tableId,
                });
              } else {
                list.push(formattedDimensionElement);
              }
            });
          } else if (currentDimensionId === 'adrielCampaign' && dimensionId === 'campaignDimension') {
            // we don't want to wrap the campaigns in a unlabelled dimension element if the dimension is adrielCampaign
            fillList({
              tree,
              list,
              dimensionIds: [],
              parentElementTableId,
            });
          } else {
            // we create an unlabelled dimensionElement
            emptyDimensionNode[dimensionId] = dimensionElementObj;
            if (dimensionId === 'campaignDimension') {
              const campaignIds = dimensionElementObj.campaignIds;
              if (campaignIds) emptyDimensionElementCampaignIds.push(...campaignIds);
            } else {
              Object.values(dimensionElementObj).forEach(({ campaignIds }) => {
                if (campaignIds) emptyDimensionElementCampaignIds.push(...campaignIds);
              });
            }
          }
        });
        // create empty dimension element
        if (emptyDimensionElementCampaignIds.length > 0) {
          const campaigns = emptyDimensionElementCampaignIds.map(campaignId => this.campaignsById[campaignId]);
          const children = [];
          const id = `${currentDimension.id}:unlabelled`;
          let tableId = id;
          if (parentElementTableId) tableId += '-' + parentElementTableId;
          const emptyDimensionElement = {
            id,
            tableId,
            name: `unlabelled`,
            unlabelled: true,
            level: 'customDimension',
            dimension: {
              id: currentDimension.id,
              name: currentDimension.name,
            },
            nbCampaigns: emptyDimensionElementCampaignIds.length,
            children,
            cell: metricUtils.getMergedCellFromCampaigns({
              campaigns,
              availableMetrics: this.availableMetrics,
            }),
          };
          list.push(emptyDimensionElement);
          return fillList({
            tree: emptyDimensionNode,
            list: children,
            dimensionIds: restDimensionIds,
            parentElementTableId: tableId,
          });
        }
      }
    };
    fillList({ tree: dimensionTree, list: campaignsPerDimensionList, dimensionIds });
    return campaignsPerDimensionList;
  }

  getStructureForAction(level, channelId) {
    console.warn('getStructureForAction is deprecated.');
    let campaign;
    let adSets;
    let ads;
    if (level === 'campaign') {
      campaign = this.campaigns.find(campaign => campaign.channelId == channelId);
      adSets = campaign.adSets;
      ads = campaign.ads;
    } else if (level === 'adSet') {
      const adSet = this.adSets.find(adSet => adSet.channelId == channelId);
      campaign = adSet.campaign;
      adSets = [adSet];
      ads = adSet.ads;
    } else if (level === 'ad') {
      const ad = this.ads.find(ad => ad.channelId == channelId);
      const adSet = ad.adSet;
      campaign = adSet.campaign;
      adSets = [adSet];
      ads = [ad];
    }
    const adAccount = campaign.adAccount;
    const obj = {
      adAccount,
      campaign,
      adSets,
      ads,
    };
    return obj;
  }

  // obsolete
  getFormattedForAction(level, channelId) {
    console.warn('getFormattedForAction is deprecated.');
    const obj = this.getStructureForAction(level, channelId);
    return Object.entries(obj).reduce((acc, [attr, element]) => {
      if (Array.isArray(element)) {
        acc[attr] = element.map(row => row.toJSON());
      } else acc[attr] = element.toJSON();
      return acc;
    }, {});
  }

  getJSON() {
    const jsonObj = {
      adAccounts: this.adAccounts.map(adAccount => adAccount.getJSON()),
      adrielCampaigns: this.adrielCampaigns.map(adrielCampaign => adrielCampaign.getJSON()),
      campaigns: this.campaigns.map(campaign => campaign.getJSON()),
      adSets: this.adSets.map(adSet => adSet.getJSON()),
      ads: this.ads.map(ad => ad.getJSON()),
    };
    return jsonObj;
  }
}

Object.assign(AdAccountArchitecture, require('../constants'));

module.exports = AdAccountArchitecture;
